import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, ButtonBase, CircularProgress, Dialog, IconButton, InputBase, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography, useMediaQuery } from "@mui/material";
import {
  Assignment, AssignmentIndOutlined, Check, Close, ContentCopy, Dashboard, EditNote, Event, Flag, Folder,
  HeadsetMic, History, MedicalInformation, Mic, PersonSearch, ReceiptLong, School, Search, Timeline,
} from "@mui/icons-material";
import { ActionChips, AreaHeader, AssistantNote, AuditNotice, ComposerFrame, ComposerIconButton, conversationSx, FocusBar, HeaderBadge, HeaderLink, listChipSx, listColumnSx, listHeaderSx, listSearchSx, PendingAction, SendButton, SpeakerLabel, TopicList, VoiceStatus } from "../components/assistants/AssistantUi";
import { useDictation } from "../components/assistants/useDictation";
import { useSpeechReply } from "../components/assistants/useSpeechReply";
import { voiceService } from "../services/voiceService";
import HeaderInternal from "../components/Header/HeaderInternal";
import ConfirmModal from "../components/modals/ConfirmModal";
import { useAuth } from "../contexts/AuthContext";
import { openPageInNewTab, useNavigation } from "../contexts/RouterContext";
import { colors, shadows } from "../theme/designSystem";
import { aiChatService, ChatMessage } from "../services/aiChatService";
import { patientAssistantService, PatientAssistantData, PatientAssistantPatient, PatientAssistantSession } from "../services/patientAssistantService";
import { patientsService } from "../services/patientsService";
import { formatWhen, parseAssistant, renderMarkdown } from "../components/chat/AgentConversation";

/**
 * Assistente do prontuário (item 206, docs/arquitetura/agentes-ia/chat-do-paciente.md): lista dos
 * pacientes do profissional à esquerda e, à direita, as sessões abertas em abas — cada aba presa a um
 * paciente só (D-PAC-1). O paciente vai no envio só ao abrir a sessão; depois o servidor usa o da
 * conversa e recusa qualquer outro.
 */

// Assuntos por aba do prontuário (D-PAC-11): só aparecem as abas que o perfil já abre, pela mesma
// permissão do cadastro do paciente (TAB_PERMISSIONS, RN-527). Cadastro e Acesso a dados ficam de fora.
const TOPICS: { title: string; does: string; permission: string; icon: React.ReactElement }[] = [
  { title: "Visão geral", does: "resumo do que está registrado", permission: "patient-overview", icon: <Dashboard /> },
  { title: "Evoluções", does: "evoluções pendentes e anteriores", permission: "patient-evolutions", icon: <EditNote /> },
  { title: "Plano terapêutico", does: "objetivos e metas", permission: "patient-therapy-plan", icon: <Flag /> },
  { title: "Avaliações", does: "formulários e resultados", permission: "patient-assessments", icon: <Assignment /> },
  { title: "Diagnóstico", does: "diagnósticos e CID registrados", permission: "patient-diagnosis", icon: <MedicalInformation /> },
  { title: "Agenda", does: "próximas sessões e histórico", permission: "schedule", icon: <Event /> },
  { title: "Arquivos", does: "documentos do paciente", permission: "patient-files", icon: <Folder /> },
  { title: "Receituário", does: "receitas registradas", permission: "patient-prescriptions", icon: <ReceiptLong /> },
  { title: "Vínculos", does: "escola e instituições", permission: "patient-links", icon: <School /> },
  { title: "Linha do tempo", does: "o histórico em ordem", permission: "patient-timeline", icon: <Timeline /> },
];

const PRIVACY_NOTE = "A IA conhece só o primeiro nome e as iniciais; documentos e contatos não saem do sistema";

type Mode = "new" | "continue" | "read";
// Resposta cortada pelo teto de saída do modelo: a mensagem avisa, como no chat de IA
type Msg = ChatMessage & { truncated?: boolean };
type Tab = {
  patient: PatientAssistantPatient;
  mode: Mode;
  conversationId: string | null;
  messages: Msg[];
  draft: string;
  loading: boolean;
  sending: boolean;
  error: string | null;
  // Sessões anteriores mostradas na aba em branco: undefined = não buscou, null = buscando
  past?: PatientAssistantSession[] | null;
};

const PAST_SHOWN = 3;

const ageOf = (birth: string | null): string => {
  if (!birth) return "";
  const b = new Date(birth);
  if (Number.isNaN(b.getTime())) return "";
  const now = new Date();
  let years = now.getFullYear() - b.getFullYear();
  if (now < new Date(now.getFullYear(), b.getMonth(), b.getDate())) years--;
  return years <= 0 ? "menos de 1 ano" : `${years} ${years === 1 ? "ano" : "anos"}`;
};
const dateBr = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "");
const initials = (name: string): string =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("");
const shortId = (id: string): string => id.slice(0, 4).toUpperCase();
const firstName = (name: string): string => name.split(" ")[0];
// "Ana Luiza de Castro Melo" → "Ana L. de C. M.": cabe na aba e ainda distingue homônimos
const shortName = (name: string): string => {
  const [first, ...rest] = name.split(/\s+/).filter(Boolean);
  return [first, ...rest.map((w) => (/^(da|de|do|das|dos|e)$/i.test(w) ? w.toLowerCase() : `${w[0].toUpperCase()}.`))].join(" ");
};

const newTab = (patient: PatientAssistantPatient): Tab =>
  ({ patient, mode: "new", conversationId: null, messages: [], draft: "", loading: false, sending: false, error: null });

// ---------- Mensagens: sem balão — quem fala numa linha e o texto embaixo (molde dos assistentes) ----------
type MessageRowProps = {
  m: Msg;
  index: number;
  speakerName: string;
  userInitial: string;
  readOnly: boolean;
  sending: boolean;
  isMobile: boolean;
  copied: boolean;
  onAction: (text: string) => void;
  onCopy: (text: string, index: number) => void;
};

// Memorizada: o rascunho mora no estado das abas, e sem isto cada tecla (ou resultado parcial do
// ditado) refazia o markdown de todas as mensagens da conversa.
const MessageRow = React.memo(function MessageRow({ m, index, speakerName, userInitial, readOnly, sending, isMobile, copied, onAction, onCopy }: MessageRowProps) {
  const isUser = m.role === "user";
  const { text, actions } = isUser ? { text: m.content, actions: [] as string[] } : parseAssistant(m.content);
  // Molde dos assistentes: quem fala numa linha, o conteúdo embaixo na largura toda (sem a coluna do avatar)
  return (
    <Box>
      <SpeakerLabel isUser={isUser} initial={userInitial} name={speakerName} />
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ fontSize: "0.9375rem", lineHeight: 1.55, color: colors.textStrong, wordBreak: "break-word", whiteSpace: isUser ? "pre-wrap" : "normal" }}>
          {isUser ? <Box className="c4-text">{text}</Box> : renderMarkdown(text)}
        </Box>
        {!readOnly && <ActionChips actions={actions} onPick={onAction} isMobile={isMobile} disabled={sending} />}
        {m.truncated && (
          <Box sx={{ mt: 1, px: 1, py: 0.5, bgcolor: colors.warningSoft, border: `1px solid ${colors.warning}`, borderRadius: "8px" }}>
            <Typography sx={{ fontSize: "0.75rem", color: colors.warningSoftText, lineHeight: 1.35 }}>A resposta foi interrompida por atingir o limite de tamanho. Envie "continuar" para ver o restante.</Typography>
          </Box>
        )}
        {!isUser && (
          <Tooltip title={copied ? "Copiado" : "Copiar resposta"}>
            <IconButton size="small" onClick={() => onCopy(text, index)} aria-label={copied ? "Resposta copiada" : "Copiar resposta"}
              sx={{ color: colors.textSecondary, mt: 0.5, width: isMobile ? 44 : 28, height: isMobile ? 44 : 28, borderRadius: "8px" }}>
              {copied ? <Check sx={{ fontSize: "0.95rem" }} /> : <ContentCopy sx={{ fontSize: "0.95rem" }} />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
});

// embedded: dentro da página Assistentes (D-OP-16), sem header nem casca de página próprios
const PatientAssistant: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { user } = useAuth();
  const { goToDashboard } = useNavigation();
  const isMobile = useMediaQuery("(max-width:900px)");

  const [data, setData] = useState<PatientAssistantData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "pending" | "active">("all");
  // Busca além da lista dos 90 dias: pacientes que o profissional pode acessar e não atendeu recentemente
  const [others, setOthers] = useState<PatientAssistantPatient[]>([]);
  const [searchingOthers, setSearchingOthers] = useState(false);

  const [tabs, setTabs] = useState<Tab[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [mobileList, setMobileList] = useState(true);
  // Celular: as sessões abertas ficam num seletor no nome do paciente (molde dos assistentes)
  const [switcherEl, setSwitcherEl] = useState<HTMLElement | null>(null);
  const [photos, setPhotos] = useState<Record<string, string>>({});
  const [history, setHistory] = useState<PatientAssistantSession[] | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  // Fechar a aba sempre pergunta: com conversa em andamento, fechar ENCERRA a sessão (fica só de leitura)
  const [closingId, setClosingId] = useState<string | null>(null);
  const [ending, setEnding] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const tab = tabs.find((t) => t.patient.id === activeId) ?? null;
  const patchTab = (patientId: string, patch: Partial<Tab> | ((t: Tab) => Partial<Tab>)) =>
    setTabs((list) => list.map((t) => (t.patient.id === patientId ? { ...t, ...(typeof patch === "function" ? patch(t) : patch) } : t)));

  useEffect(() => {
    if (!embedded) document.title = "Clinic4Us - Assistente do prontuário";
  }, [embedded]);

  useEffect(() => {
    patientAssistantService.get()
      .then(setData)
      .catch(() => setLoadError("Não foi possível carregar os seus pacientes."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const term = search.trim();
    if (term.length < 3) { setOthers([]); setSearchingOthers(false); return; }
    setSearchingOthers(true);
    const timer = window.setTimeout(() => {
      patientAssistantService.get(term)
        .then((d) => setOthers(d.patients))
        .catch(() => setOthers([]))
        .finally(() => setSearchingOthers(false));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  // A foto só aparece na identificação: busca a miniatura de quem tem aba aberta. Os object URLs são
  // revogados ao sair da página.
  const photoUrls = useRef<string[]>([]);
  useEffect(() => () => photoUrls.current.forEach((u) => URL.revokeObjectURL(u)), []);
  useEffect(() => {
    tabs.filter((t) => t.patient.hasPhoto && !photos[t.patient.id]).forEach((t) => {
      patientsService.getPhoto(t.patient.id, true)
        .then((url) => { photoUrls.current.push(url); setPhotos((m) => ({ ...m, [t.patient.id]: url })); })
        .catch(() => undefined);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs.length]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [tab?.messages.length, tab?.sending, activeId]);

  // Aba em branco: busca as sessões anteriores do paciente para ler sem passar pelo Histórico
  useEffect(() => {
    if (!tab || tab.mode !== "new" || tab.loading || tab.messages.length > 0 || tab.past !== undefined) return;
    const pid = tab.patient.id;
    patchTab(pid, { past: null });
    patientAssistantService.sessions(pid)
      .then((list) => patchTab(pid, { past: list }))
      .catch(() => patchTab(pid, { past: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab?.patient.id, tab?.mode, tab?.loading, tab?.messages.length, tab?.past]);

  // Sessão ativa (não encerrada, com atividade nas últimas 36 h) abre carregada, em modo continuar
  const resumeLatest = async (patientId: string) => {
    try {
      const active = (await patientAssistantService.sessions(patientId)).find((s) => s.active);
      if (!active) { patchTab(patientId, { loading: false }); return; }
      const msgs = await aiChatService.messages(active.id);
      patchTab(patientId, { mode: "continue", conversationId: active.id, messages: msgs, loading: false });
    } catch {
      patchTab(patientId, { loading: false, error: "Não foi possível retomar a sessão." });
    }
  };

  const openPatient = (p: PatientAssistantPatient) => {
    if (!tabs.some((t) => t.patient.id === p.id)) {
      setTabs((list) => [...list, { ...newTab(p), loading: p.hasActiveSession }]);
      if (p.hasActiveSession) resumeLatest(p.id);
    }
    setActiveId(p.id);
    setMobileList(false);
  };

  // Aba aberta = sessão ativa: ao entrar, toda sessão ativa abre como aba, na ordem da última visita
  // (guardada no navegador, por usuário, só ids) e depois pela atividade mais recente.
  const storageKey = user?.userId ? `clinic4us-patient-assistant-tabs:${user.userId}` : null;
  const restoredRef = useRef(false);
  useEffect(() => {
    if (!data || !storageKey || restoredRef.current) return;
    restoredRef.current = true;
    let saved: { ids?: string[]; active?: string | null } | null = null;
    try { saved = JSON.parse(localStorage.getItem(storageKey) || "null"); } catch { saved = null; }
    const order = saved?.ids ?? [];
    const rank = (p: PatientAssistantPatient) => { const i = order.indexOf(p.id); return i < 0 ? Number.MAX_SAFE_INTEGER : i; };
    const reopen = data.patients
      .filter((p) => p.hasActiveSession)
      .sort((a, b) => rank(a) - rank(b) || (b.lastSessionAt ?? "").localeCompare(a.lastSessionAt ?? ""));
    if (reopen.length === 0) return;
    setTabs(reopen.map((p) => ({ ...newTab(p), loading: true })));
    setActiveId(reopen.some((p) => p.id === saved?.active) ? saved!.active! : reopen[0].id);
    reopen.forEach((p) => resumeLatest(p.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, storageKey]);

  const tabIds = tabs.map((t) => t.patient.id).join(",");
  useEffect(() => {
    if (!storageKey || !restoredRef.current) return;
    try { localStorage.setItem(storageKey, JSON.stringify({ ids: tabIds ? tabIds.split(",") : [], active: activeId })); } catch { /* sem storage */ }
  }, [tabIds, activeId, storageKey]);

  const closeTab = (patientId: string) => {
    if (listening && patientId === activeId) stopVoice();
    const i = tabs.findIndex((t) => t.patient.id === patientId);
    const rest = tabs.filter((t) => t.patient.id !== patientId);
    setTabs(rest);
    if (activeId === patientId) {
      const next = rest[Math.max(0, i - 1)] ?? null;
      setActiveId(next?.patient.id ?? null);
      if (!next) setMobileList(true);
    }
  };

  const requestClose = (t: Tab) => setClosingId(t.patient.id);

  const confirmClose = async () => {
    const t = tabs.find((x) => x.patient.id === closingId);
    if (!t) { setClosingId(null); return; }
    if (t.conversationId && t.mode !== "read") {
      setEnding(true);
      try {
        await patientAssistantService.endSession(t.conversationId);
      } catch {
        setEnding(false); setClosingId(null);
        patchTab(t.patient.id, { error: "Não foi possível encerrar a sessão. Tente de novo." });
        return;
      }
      setEnding(false);
      const off = (p: PatientAssistantPatient) => (p.id === t.patient.id ? { ...p, hasActiveSession: false } : p);
      setData((d) => (d ? { ...d, patients: d.patients.map(off) } : d));
      setOthers((list) => list.map(off));
    }
    closeTab(t.patient.id);
    setClosingId(null);
  };

  // Setas trocam de aba e Home/End vão às pontas (padrão WAI-ARIA de abas)
  const onTabKeyDown = (e: React.KeyboardEvent, index: number) => {
    const last = tabs.length - 1;
    const target = e.key === "ArrowRight" ? (index === last ? 0 : index + 1)
      : e.key === "ArrowLeft" ? (index === 0 ? last : index - 1)
      : e.key === "Home" ? 0 : e.key === "End" ? last : null;
    if (target === null) return;
    e.preventDefault();
    const id = tabs[target].patient.id;
    setActiveId(id);
    tabRefs.current[id]?.focus();
  };

  const openHistory = async () => {
    if (!tab) return;
    setHistoryOpen(true); setHistory(null);
    try { setHistory(await patientAssistantService.sessions(tab.patient.id)); } catch { setHistory([]); }
  };

  const openSession = async (s: PatientAssistantSession, mode: Mode) => {
    if (!tab) return;
    const pid = tab.patient.id;
    setHistoryOpen(false);
    patchTab(pid, { mode, conversationId: s.id, messages: [], draft: "", loading: true, error: null });
    try {
      const msgs = await aiChatService.messages(s.id);
      patchTab(pid, { messages: msgs, loading: false });
    } catch {
      patchTab(pid, { loading: false, error: "Não foi possível abrir a sessão." });
    }
  };

  const restart = () => { if (tab) { setHistoryOpen(false); patchTab(tab.patient.id, newTab(tab.patient)); } };

  const send = async (text?: string) => {
    if (!tab) return;
    const message = (text ?? tab.draft).trim();
    if (!message || !data?.agentId || tab.sending || tab.mode === "read") return;
    const pid = tab.patient.id;
    const convId = tab.conversationId;
    patchTab(pid, (t) => ({ sending: true, error: null, draft: "", messages: [...t.messages, { role: "user", content: message, createdAt: new Date().toISOString() }] }));
    talk.cancel();
    speech.stop();
    let reply = "";
    try {
      const res = await aiChatService.send(data.agentId, convId, message, null, convId ? null : pid);
      reply = res.reply;
      patchTab(pid, (t) => ({
        sending: false, conversationId: res.conversationId, mode: "continue",
        messages: [...t.messages, { role: "assistant", content: res.reply, createdAt: new Date().toISOString(), truncated: res.truncated }],
      }));
    } catch (e) {
      const d = (e as { response?: { data?: { description?: string } } })?.response?.data;
      patchTab(pid, (t) => ({ sending: false, draft: message, error: d?.description || "A IA não respondeu. Tente de novo.", messages: t.messages.slice(0, -1) }));
    }
    // Modo conversa: fala a resposta e volta a ouvir (se a pessoa já reabriu o microfone, start não faz nada)
    if (!reply || !talkingRef.current) return;
    const spoken = parseAssistant(reply).text;
    if (spoken) await speech.speakBrief(spoken);
    if (talkingRef.current) talk.start("");
  };
  // Ref estável para as mensagens memorizadas chamarem sempre o send da aba atual
  const sendRef = useRef(send);
  sendRef.current = send;
  const onAction = useCallback((a: string) => { sendRef.current(a); }, []);

  // Modo conversa (RN-570, D-OP-34): ouve → envia na pausa → fala a resposta → volta a ouvir. Usa o motor de
  // transcrição da clínica; o ditado manual abaixo continua como sempre foi.
  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;
  const [voiceReady, setVoiceReady] = useState(false);
  const [talking, setTalking] = useState(false);
  const talkingRef = useRef(false);
  const reportError = (message: string) => { if (activeIdRef.current) patchTab(activeIdRef.current, { error: message }); };
  const speech = useSpeechReply("prontuario", reportError);
  const talk = useDictation(tab?.draft ?? "", (text) => { if (activeIdRef.current) patchTab(activeIdRef.current, { draft: text }); },
    reportError, talking ? (text) => { void onVoice(text); } : undefined);

  // Fala no modo conversa: "sim"/"não" respondem ao "Quer que eu leia o restante?"; o resto é pedido novo
  const onVoice = async (text: string) => {
    const answer = await speech.answerFollowUp(text);
    if (answer === null) {
      sendRef.current(text);
      return;
    }
    if (activeIdRef.current) patchTab(activeIdRef.current, { draft: "" });
    if (talkingRef.current) talk.start("");
  };

  useEffect(() => {
    if (!data?.speechReply) {
      setVoiceReady(false);
      return;
    }
    let alive = true;
    voiceService.getSettings()
      .then((v) => { if (alive) setVoiceReady(v.speechReady); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [data?.speechReply]);

  const stopTalking = () => {
    talkingRef.current = false;
    setTalking(false);
    speech.stop();
    talk.cancel();
  };

  // Trocar de paciente encerra o modo conversa: a voz nunca continua na aba de outro paciente
  useEffect(() => {
    if (talkingRef.current) stopTalking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const toggleTalking = () => {
    if (talkingRef.current) {
      stopTalking();
      return;
    }
    if (listening) stopVoice();
    speech.unlock();
    talkingRef.current = true;
    setTalking(true);
    talk.start(tab?.draft ?? "");
  };

  // Microfone no modo conversa: durante a fala interrompe a voz e passa a ouvir
  const handleTalkMic = () => {
    if (speech.speaking) {
      speech.stop();
      talk.start("");
      return;
    }
    talk.toggle();
  };

  // Ditado por voz (Web Speech, nativo do navegador; Firefox não tem). Fica gravando até o
  // profissional clicar de novo ou até 15 s sem fala: o reconhecimento do navegador encerra sozinho
  // a cada pausa, então é reaberto enquanto o ditado estiver ligado, somando ao que já foi transcrito.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const SpeechRec: any = typeof window !== "undefined" ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition : null;
  const recRef = useRef<{ stop: () => void; start: () => void } | null>(null);
  const wantRef = useRef(false);
  const silenceRef = useRef<number | null>(null);
  const baseRef = useRef("");
  const textRef = useRef("");
  const [listening, setListening] = useState(false);
  const SILENCE_MS = 15000;

  const stopVoice = () => {
    wantRef.current = false;
    if (silenceRef.current) window.clearTimeout(silenceRef.current);
    silenceRef.current = null;
    recRef.current?.stop();
    setListening(false);
  };
  const armSilence = () => {
    if (silenceRef.current) window.clearTimeout(silenceRef.current);
    silenceRef.current = window.setTimeout(stopVoice, SILENCE_MS);
  };
  useEffect(() => () => { wantRef.current = false; recRef.current?.stop(); if (silenceRef.current) window.clearTimeout(silenceRef.current); }, []);

  const toggleVoice = async () => {
    if (!tab) return;
    const pid = tab.patient.id;
    if (!SpeechRec) { patchTab(pid, { error: "Ditado por voz não é suportado neste navegador. Use Chrome, Edge ou Safari." }); return; }
    if (listening) { stopVoice(); return; }
    try {
      if (navigator.mediaDevices?.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
      }
      const rec = new SpeechRec();
      rec.lang = "pt-BR"; rec.interimResults = true; rec.continuous = true;
      baseRef.current = tab.draft.trim() ? `${tab.draft.trim()} ` : "";
      textRef.current = baseRef.current;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      rec.onresult = (e: any) => {
        let t = "";
        for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript;
        textRef.current = baseRef.current + t.trim();
        patchTab(pid, { draft: textRef.current });
        armSilence();
      };
      rec.onend = () => {
        if (wantRef.current) {
          // Pausa encerrou a sessão do navegador: reabre sem perder o que foi dito
          baseRef.current = textRef.current.trim() ? `${textRef.current.trim()} ` : "";
          try { rec.start(); } catch { stopVoice(); }
        } else {
          setListening(false);
        }
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      rec.onerror = (e: any) => { if (e?.error !== "no-speech" && e?.error !== "aborted") stopVoice(); };
      recRef.current = rec; wantRef.current = true; setListening(true); armSilence(); rec.start();
    } catch {
      stopVoice(); patchTab(pid, { error: "Permita o acesso ao microfone no navegador para ditar por voz." });
    }
  };

  const onCopy = useCallback(async (text: string, index: number) => {
    try { await navigator.clipboard.writeText(text); setCopied(String(index)); window.setTimeout(() => setCopied(null), 1500); } catch { /* sem clipboard */ }
  }, []);

  const allowed = useMemo(() => new Set(user?.allowedPages ?? []), [user]);
  const topics = TOPICS.filter((t) => allowed.has(t.permission));
  const userInitial = (user?.email || "P").trim()[0]?.toUpperCase() ?? "P";

  const patients = useMemo(() => {
    const q = search.trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    return (data?.patients ?? []).filter((p) =>
      (!q || p.name.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").includes(q))
      && (filter === "all" || (filter === "pending" ? p.pendingEvolutions > 0 : p.hasActiveSession)));
  }, [data, search, filter]);

  const readOnly = tab?.mode === "read";
  const canWrite = !!tab && !readOnly && !!data?.agentId;
  const agentName = data?.agentName || "Assistente";
  const closingTab = tabs.find((t) => t.patient.id === closingId) ?? null;
  // Aba em branco (nada enviado) ou sessão já só de leitura: fechar só tira a aba da tela
  const closingEnds = !!closingTab?.conversationId && closingTab.mode !== "read";
  const closingName = closingTab ? firstName(closingTab.patient.name) : "";

  // ---------- Lista de pacientes (sem foto: a foto fica só na identificação da sessão) ----------
  const renderPatient = (p: PatientAssistantPatient) => {
    const isSel = tab?.patient.id === p.id;
    const hasTab = tabs.some((t) => t.patient.id === p.id);
    return (
      <Box key={p.id} component="button" type="button" onClick={() => openPatient(p)} aria-current={isSel ? "true" : undefined}
        sx={{
          width: "100%", textAlign: "left", border: "none", borderBottom: `1px solid ${colors.backgroundSubtle}`, fontFamily: "inherit", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 1.25, p: "10px 16px", minHeight: 56,
          bgcolor: isSel ? colors.primaryLight : "transparent", boxShadow: isSel ? `inset 3px 0 0 ${colors.primary}` : "none",
          "&:hover": { bgcolor: colors.primaryLight },
        }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: colors.textStrong, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: "0.75rem", color: colors.textSecondary }}>
            <span>{ageOf(p.birthDate)}</span>
            {(hasTab || p.hasActiveSession) && (
              <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5, color: colors.successHover }}>
                <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: colors.success }} />
                {hasTab ? "sessão aberta" : "sessão ativa"}
              </Box>
            )}
          </Box>
        </Box>
        {p.pendingEvolutions > 0 && (
          <Tooltip title="Evoluções pendentes" enterTouchDelay={0}>
            <Box role="img" aria-label={`${p.pendingEvolutions} ${p.pendingEvolutions === 1 ? "evolução pendente" : "evoluções pendentes"}`}
              sx={{ height: 20, minWidth: 20, px: 0.6, borderRadius: "999px", bgcolor: colors.warningSoft, color: colors.warningSoftText, fontSize: "0.75rem", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{p.pendingEvolutions}</Box>
          </Tooltip>
        )}
      </Box>
    );
  };

  const explorer = (
    <Box sx={{ ...listColumnSx, ...(isMobile ? { width: "100%", borderRight: "none" } : {}) }}>
      <Box sx={listHeaderSx}>
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
          <Typography component="h1" sx={{ fontSize: "1rem", fontWeight: 600, color: colors.textStrong, whiteSpace: "nowrap" }}>Meus pacientes</Typography>
          <Typography sx={{ fontSize: "0.75rem", color: colors.textSecondary, whiteSpace: "nowrap" }}>{data?.patients.length ?? 0} em atendimento</Typography>
        </Box>
        <Box sx={listSearchSx}>
          <Search sx={{ fontSize: 18, color: colors.textSecondary }} aria-hidden />
          <InputBase value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar paciente"
            inputProps={{ "aria-label": "Buscar paciente", maxLength: 80 }} sx={{ flex: 1, fontSize: "0.875rem" }} />
        </Box>
        <Box role="group" aria-label="Filtrar pacientes" sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
          {([["all", "Todos"], ["pending", "Com pendência"], ["active", "Sessão ativa"]] as const).map(([k, label]) => (
            <ButtonBase key={k} onClick={() => setFilter(k)} aria-pressed={filter === k}
              sx={{ ...listChipSx(filter === k), ...(isMobile ? { minHeight: 44 } : {}) }}>
              {label}
            </ButtonBase>
          ))}
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, overflow: "auto" }}>
        {loading && <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={24} sx={{ color: colors.primary }} /></Box>}
        {!loading && patients.length === 0 && <Typography sx={{ fontSize: "0.875rem", color: colors.textSecondary, p: 2, textAlign: "center" }}>Nenhum paciente encontrado.</Typography>}
        {patients.map(renderPatient)}
        {search.trim().length >= 3 && (searchingOthers || others.length > 0) && (
          <Box sx={{ mt: 1 }}>
            <Typography component="h2" sx={{ fontSize: "0.75rem", fontWeight: 600, color: colors.textSecondary, px: 2, py: 1 }}>Outros pacientes · sem sessão nos últimos 90 dias</Typography>
            {searchingOthers ? <Box sx={{ py: 1, display: "flex", justifyContent: "center" }}><CircularProgress size={16} /></Box> : others.map(renderPatient)}
          </Box>
        )}
      </Box>
    </Box>
  );

  // Abertura no molde dos assistentes: pendência em destaque primeiro, depois a lista de assuntos
  const opening = tab && tab.mode === "new" && !tab.loading && tab.messages.length === 0 && (
    <Box>
      <SpeakerLabel isUser={false} initial={userInitial} name={agentName} />
      <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Typography sx={{ fontSize: "1rem", lineHeight: 1.5, color: colors.textStrong }}>Sobre {firstName(tab.patient.name)}, o que tratar agora?</Typography>
        {tab.patient.pendingEvolutions > 0 && (
          <PendingAction icon={<EditNote />} disabled={!canWrite} onClick={() => send("Quais evoluções estão pendentes?")}
            title={tab.patient.pendingEvolutions === 1 ? "1 evolução pendente" : `${tab.patient.pendingEvolutions} evoluções pendentes`}
            detail="toque para ver e registrar" />
        )}
        {topics.length > 0 && (
          <TopicList isMobile={isMobile} disabled={!canWrite}
            topics={topics.map((t) => ({ title: t.title, does: t.does, icon: t.icon, onClick: () => send(`Quero tratar de ${t.title.toLowerCase()}.`) }))} />
        )}
        {tab.past && tab.past.length > 0 && (
          <Box component="section" aria-labelledby="pa-past-title" sx={{ mt: 2.5 }}>
            <Typography id="pa-past-title" component="h3" sx={{ fontSize: "0.75rem", fontWeight: 600, color: colors.textSecondary, mb: 0.75 }}>Sessões anteriores</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, maxWidth: 560 }}>
              {tab.past.slice(0, PAST_SHOWN).map((s) => (
                <Box key={s.id} sx={{ display: "flex", alignItems: "center", gap: 1.25, border: `1px solid ${colors.backgroundAlt}`, borderRadius: "8px", p: "8px 8px 8px 12px" }}>
                  <Box aria-hidden sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: s.active ? colors.success : colors.borderHover, flexShrink: 0 }} />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: "0.875rem", color: colors.textStrong, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.title || "Sessão"}</Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: colors.textSecondary }}>{formatWhen(s.updatedAt)} · {s.active ? "ativa" : "encerrada"}</Typography>
                  </Box>
                  <Button size="small" variant="outlined" onClick={() => openSession(s, "read")} aria-label={`Ler a sessão ${s.title || ""} de ${formatWhen(s.updatedAt)}`}
                    sx={{ minWidth: 0, px: 1.5, height: isMobile ? 44 : 32, borderColor: colors.border, color: colors.textPrimary, fontSize: "0.8125rem", fontWeight: 500, flexShrink: 0 }}>Ler</Button>
                </Box>
              ))}
            </Box>
            {tab.past.length > PAST_SHOWN && (
              <Box component="button" type="button" onClick={openHistory}
                sx={{ mt: 0.75, border: "none", background: "none", p: 0, minHeight: isMobile ? 44 : "auto", color: colors.primaryText, fontWeight: 500, fontSize: "0.8125rem", cursor: "pointer", fontFamily: "inherit",
                  textDecoration: "underline", textUnderlineOffset: "3px", textDecorationColor: colors.primarySoft, "&:hover": { textDecorationColor: colors.primaryText } }}>
                Ver todas ({tab.past.length})
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );

  // ---------- Abas das sessões abertas (tablist WAI-ARIA: Tab entra na aba ativa, setas trocam) ----------
  const tabItems = tabs.map((t, i) => {
    const on = t.patient.id === activeId;
    const tabButton = (
      <Box component="button" type="button" role="tab" id={`pa-tab-${t.patient.id}`} aria-selected={on} aria-controls="pa-tabpanel"
        tabIndex={on ? 0 : -1} ref={(el: HTMLButtonElement | null) => { tabRefs.current[t.patient.id] = el; }}
        onClick={() => setActiveId(t.patient.id)} onKeyDown={(e: React.KeyboardEvent) => onTabKeyDown(e, i)} title={t.patient.name}
        sx={{ border: "none", background: "none", p: 0, fontFamily: "inherit", color: "inherit", cursor: "pointer", fontSize: "0.8125rem", fontWeight: 500,
          maxWidth: 190, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", alignSelf: "stretch", display: "flex", alignItems: "center" }}>
        {shortName(t.patient.name)}
      </Box>
    );
    const closeButton = (
      <IconButton aria-label={`Fechar sessão de ${shortName(t.patient.name)}`} onClick={() => requestClose(t)}
        sx={{ width: 28, height: 28, borderRadius: "8px", color: colors.textSecondary }}>
        <Close sx={{ fontSize: 16 }} />
      </IconButton>
    );
    // Só no desktop: no celular as sessões abertas ficam no seletor da barra do foco
    return (
      <Box key={t.patient.id} sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 0.5, pl: 2, pr: 0.75, borderRight: `1px solid ${colors.backgroundAlt}`,
        color: on ? colors.textStrong : colors.textPrimary, bgcolor: on ? colors.white : "transparent", boxShadow: on ? `inset 0 2px 0 ${colors.primary}` : "none", "&:hover": { bgcolor: colors.white } }}>
        {tabButton}
        <Tooltip title="Fechar sessão">{closeButton}</Tooltip>
      </Box>
    );
  });

  const tabBar = (
    <Box sx={{ height: 44, flexShrink: 0, display: "flex", bgcolor: colors.background, borderBottom: `1px solid ${colors.backgroundAlt}` }}>
      {/* Barra fina visível: com muitas abas, é o sinal de que há mais para o lado */}
      <Box role="tablist" aria-label="Sessões abertas" sx={{ display: "flex", minWidth: 0, overflowX: "auto", scrollbarWidth: "thin",
        "&::-webkit-scrollbar": { height: 4 }, "&::-webkit-scrollbar-thumb": { bgcolor: colors.border, borderRadius: "8px" } }}>
        {tabItems}
      </Box>
      <Box sx={{ flexGrow: 1 }} />
      <Button onClick={openHistory} startIcon={<History />} disabled={!tab}
        sx={{ flexShrink: 0, height: "100%", px: 2, borderRadius: 0, color: colors.textPrimary, fontSize: "0.8125rem", fontWeight: 500, "&:hover": { bgcolor: colors.backgroundAlt } }}>
        Histórico
      </Button>
    </Box>
  );

  // ---------- Celular: barra do foco + seletor das sessões abertas ----------
  const mobileIdentity = tab && (
    <>
      <FocusBar
        avatar={photos[tab.patient.id]
          ? <img src={photos[tab.patient.id]} alt={`Foto de ${tab.patient.name}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : <span aria-hidden>{initials(tab.patient.name)}</span>}
        title={shortName(tab.patient.name)}
        meta={[ageOf(tab.patient.birthDate), `ID ${shortId(tab.patient.id)}`, readOnly ? "somente leitura" : tabs.length > 1 ? `${tabs.length} sessões abertas` : null]
          .filter(Boolean).join(" · ")}
        onTitleClick={(e) => setSwitcherEl(e.currentTarget)}
        titleAriaLabel={`${tab.patient.name}: trocar de sessão ou de paciente`}
        actions={
          <>
            <IconButton onClick={() => openPageInNewTab("patient-register", { id: tab.patient.id })} aria-label="Ver cadastro" sx={{ width: 44, height: 44, color: colors.textPrimary }}><AssignmentIndOutlined /></IconButton>
            <IconButton onClick={openHistory} aria-label="Histórico de sessões" sx={{ width: 44, height: 44, color: colors.textPrimary }}><History /></IconButton>
          </>
        }
      />
      <Menu anchorEl={switcherEl} open={!!switcherEl} onClose={() => setSwitcherEl(null)} MenuListProps={{ "aria-label": "Sessões abertas" }}
        PaperProps={{ sx: { minWidth: 280, maxWidth: "calc(100vw - 24px)", borderRadius: "8px" } }}>
        {tabs.map((t) => (
          <MenuItem key={t.patient.id} selected={t.patient.id === activeId} onClick={() => { setActiveId(t.patient.id); setSwitcherEl(null); }} sx={{ minHeight: 48, pr: 0.5 }}>
            <ListItemText primary={shortName(t.patient.name)} secondary={t.mode === "read" ? "somente leitura" : t.conversationId ? "sessão em andamento" : "nova"}
              primaryTypographyProps={{ fontSize: "0.9375rem", fontWeight: t.patient.id === activeId ? 600 : 500, color: colors.textStrong }}
              secondaryTypographyProps={{ fontSize: "0.75rem" }} />
            <IconButton aria-label={`Fechar sessão de ${shortName(t.patient.name)}`} sx={{ width: 44, height: 44, ml: 1 }}
              onClick={(e) => { e.stopPropagation(); setSwitcherEl(null); requestClose(t); }}>
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </MenuItem>
        ))}
        <MenuItem onClick={() => { setSwitcherEl(null); setMobileList(true); }} sx={{ minHeight: 48, borderTop: `1px solid ${colors.backgroundAlt}` }}>
          <ListItemIcon><PersonSearch sx={{ color: colors.primaryText }} /></ListItemIcon>
          <ListItemText primary="Escolher outro paciente" primaryTypographyProps={{ fontSize: "0.9375rem", color: colors.primaryText, fontWeight: 500 }} />
        </MenuItem>
      </Menu>
    </>
  );

  // ---------- Desktop: identificação fixa, foto antes do nome ----------
  const identity = tab && (
    <AreaHeader
      avatar={photos[tab.patient.id]
        ? <img src={photos[tab.patient.id]} alt={`Foto de ${tab.patient.name}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        : <span aria-hidden>{initials(tab.patient.name)}</span>}
      title={tab.patient.name}
      titleExtra={`ID ${shortId(tab.patient.id)}`}
      meta={<>
        {tab.patient.birthDate && <span>{ageOf(tab.patient.birthDate)} · nasc. {dateBr(tab.patient.birthDate)} ·</span>}
        <HeaderLink onClick={() => openPageInNewTab("patient-register", { id: tab.patient.id })}>Ver cadastro</HeaderLink>
        {readOnly && <span>· sessão encerrada, somente leitura</span>}
      </>}
      badge={<HeaderBadge tooltip={PRIVACY_NOTE}>Dados protegidos</HeaderBadge>}
    />
  );

  const composer = tab && (
    <Box sx={{ flexShrink: 0, px: { xs: 1.5, md: 4 }, pt: 1, pb: { xs: 1, md: 1.75 }, borderTop: isMobile ? `1px solid ${colors.backgroundAlt}` : "none" }}>
      <Box>
      <Box role="status" sx={{ fontSize: "0.75rem", color: colors.errorHover, mb: listening ? 0.5 : 0 }}>
        {listening ? "Gravando… clique no microfone para parar (para sozinho após 15 s sem fala)." : ""}
      </Box>
      <ComposerFrame isMobile={isMobile} disabled={!canWrite}>
        <InputBase fullWidth multiline minRows={isMobile ? 1 : 2} maxRows={6} value={tab.draft} disabled={!canWrite}
          placeholder={readOnly ? "Sessão encerrada: somente leitura" : `Escreva sobre ${firstName(tab.patient.name)}…`}
          onChange={(e) => patchTab(tab.patient.id, { draft: e.target.value })}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          inputProps={{ "aria-label": `Mensagem sobre ${firstName(tab.patient.name)}`, maxLength: 4000 }}
          sx={{ fontSize: "0.9375rem", lineHeight: 1.45, color: colors.textStrong, py: 0.5, pl: 1, "& textarea::placeholder": { color: colors.textSecondary, opacity: 1 } }} />
        {voiceReady && talk.supported && canWrite && (
          <ComposerIconButton label={talking ? "Sair do modo conversa" : "Modo conversa"} title={talking ? "Sair do modo conversa" : "Modo conversa: fale e ouça a resposta"}
            onClick={toggleTalking} isMobile={isMobile} pressed={talking} tone="voice">
            <HeadsetMic />
          </ComposerIconButton>
        )}
        {talking ? (
          <ComposerIconButton label={speech.speaking ? "Interromper e falar" : talk.listening ? "Parar de ouvir" : "Falar"}
            onClick={handleTalkMic} isMobile={isMobile} pressed={talk.listening} tone="recording">
            <Mic />
          </ComposerIconButton>
        ) : (
          <ComposerIconButton label={listening ? "Parar o ditado" : "Ditar"} onClick={toggleVoice} isMobile={isMobile}
            pressed={listening} tone="recording" disabled={!canWrite}>
            <Mic />
          </ComposerIconButton>
        )}
        <SendButton onClick={() => send()} disabled={!canWrite || tab.sending || !tab.draft.trim()} isMobile={isMobile} />
      </ComposerFrame>
      {talking && (
        <VoiceStatus speaking={speech.speaking} sending={tab.sending} transcribing={talk.transcribing} listening={talk.listening} />
      )}
      <AssistantNote>
        {isMobile ? "Nada é gravado sem a sua confirmação." : "A IA não faz avaliação clínica e vê só o primeiro nome e as iniciais. Nada é gravado sem a sua confirmação."}
      </AssistantNote>
      </Box>
    </Box>
  );

  const conversation = (
    <Box sx={{ flexGrow: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", bgcolor: colors.white }}>
      {tabs.length > 0 && (isMobile ? mobileIdentity : <>{tabBar}{identity}</>)}
      {tabs.length > 0 && <AuditNotice clinicName={user?.clinicName} />}
      {tab ? (
        <>
          <Box ref={scrollRef} id="pa-tabpanel" role={isMobile ? undefined : "tabpanel"} aria-labelledby={isMobile ? undefined : `pa-tab-${tab.patient.id}`}
            sx={{ flexGrow: 1, overflow: "auto", px: { xs: 2, md: 4 }, py: { xs: 2, md: 3 } }}>
            {/* role="log": o leitor de tela anuncia cada resposta nova sem roubar o foco da caixa de texto */}
            <Box role="log" aria-live="polite" aria-relevant="additions" aria-label={`Conversa sobre ${firstName(tab.patient.name)}`}
              sx={{ display: "flex", flexDirection: "column", gap: 2.5, ...conversationSx }}>
              {readOnly && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, bgcolor: colors.background, border: `1px solid ${colors.borderLight}`, borderRadius: "8px", p: "10px 14px", flexWrap: "wrap" }}>
                  <Typography sx={{ fontSize: "0.8125rem", color: colors.textPrimary, flexGrow: 1 }}>Sessão encerrada. Para seguir, abra uma sessão nova — ela começa com o que está registrado hoje.</Typography>
                  <Button variant="contained" onClick={restart} disabled={!data?.agentId} sx={{ px: 1.5, fontSize: "0.8125rem", fontWeight: 600 }}>Nova sessão</Button>
                </Box>
              )}
              {tab.loading && <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={22} sx={{ color: colors.primary }} aria-label="Abrindo a sessão" /></Box>}
              {opening}
              {tab.messages.map((m, i) => (
                <MessageRow key={i} m={m} index={i} speakerName={m.role === "user" ? "Profissional" : agentName} userInitial={userInitial}
                  readOnly={!!readOnly} sending={tab.sending} isMobile={isMobile} copied={copied === String(i)} onAction={onAction} onCopy={onCopy} />
              ))}
              {tab.sending && <Box role="status" sx={{ display: "flex", alignItems: "center", gap: 1, color: colors.textSecondary, fontSize: "0.8125rem" }}><CircularProgress size={14} aria-hidden /> consultando o prontuário…</Box>}
              {tab.error && <Alert severity="error" onClose={() => patchTab(tab.patient.id, { error: null })}>{tab.error}</Alert>}
            </Box>
          </Box>
          {composer}
        </>
      ) : (
        <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
          <Box sx={{ maxWidth: 420, textAlign: "center", display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography component="h2" sx={{ fontSize: "1.125rem", fontWeight: 600, color: colors.textStrong }}>Escolha um paciente</Typography>
            <Typography sx={{ fontSize: "0.875rem", color: colors.textSecondary }}>Cada paciente abre numa aba própria: a sessão fala só dele. Você pode manter vários abertos e alternar entre as abas.</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );

  // ---------- Histórico de sessões do paciente da aba ----------
  const historyDialog = (
    <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} fullScreen={isMobile} fullWidth maxWidth="sm" aria-labelledby="pa-history-title" PaperProps={{ sx: { borderRadius: isMobile ? 0 : "8px" } }}>
      <Box sx={{ bgcolor: colors.primary, color: colors.white, px: 2.5, py: 1.75, display: "flex", alignItems: "center", gap: 1 }}>
        <Typography id="pa-history-title" component="h2" sx={{ flexGrow: 1, fontSize: "1rem", fontWeight: 600 }}>Sessões de {tab ? shortName(tab.patient.name) : ""}</Typography>
        <IconButton onClick={() => setHistoryOpen(false)} aria-label="Fechar" sx={{ color: colors.white, width: 44, height: 44 }}><Close /></IconButton>
      </Box>
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1, minHeight: 160 }}>
        {history === null && <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={22} sx={{ color: colors.primary }} aria-label="Carregando sessões" /></Box>}
        {history?.length === 0 && <Typography sx={{ fontSize: "0.875rem", color: colors.textSecondary, textAlign: "center", py: 3 }}>Nenhuma sessão anterior com este paciente.</Typography>}
        {history?.map((s) => (
          <Box key={s.id} sx={{ display: "flex", alignItems: "center", gap: 1.25, border: `1px solid ${colors.backgroundAlt}`, borderRadius: "8px", p: "10px 12px",
            bgcolor: tab?.conversationId === s.id ? colors.primaryLight : colors.white, flexWrap: "wrap" }}>
            <Box aria-hidden sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: s.active ? colors.success : colors.borderHover, flexShrink: 0 }} />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: colors.textStrong, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.title || "Sessão"}</Typography>
              <Typography sx={{ fontSize: "0.75rem", color: colors.textSecondary }}>{formatWhen(s.updatedAt)} · {s.active ? "ativa" : "encerrada"}</Typography>
            </Box>
            <Button size="small" variant="outlined" onClick={() => openSession(s, "read")}
              sx={{ minWidth: 0, px: 1.5, height: isMobile ? 44 : 32, borderColor: colors.border, color: colors.textPrimary, fontSize: "0.8125rem", fontWeight: 500 }}>Ler</Button>
            {s.active && (
              <Button size="small" variant="contained" onClick={() => openSession(s, "continue")} disabled={!data?.agentId}
                sx={{ minWidth: 0, px: 1.5, height: isMobile ? 44 : 32, fontSize: "0.8125rem", fontWeight: 500 }}>Continuar</Button>
            )}
          </Box>
        ))}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 2, py: 1.5, borderTop: `1px solid ${colors.backgroundAlt}`, bgcolor: colors.background }}>
        <Button variant="outlined" onClick={() => setHistoryOpen(false)} sx={{ px: 2, borderWidth: 2, "&:hover": { borderWidth: 2 }, color: colors.primaryText, fontSize: "0.875rem", fontWeight: 500 }}>Fechar</Button>
        <Button variant="contained" onClick={restart} disabled={!data?.agentId} sx={{ px: 2, fontSize: "0.875rem", fontWeight: 500 }}>Nova sessão</Button>
      </Box>
    </Dialog>
  );

  const handleRevalidateLogin = () => {
    localStorage.removeItem("clinic4us-user-session");
    localStorage.removeItem("clinic4us-remember-me");
    window.location.href = "/?page=login";
  };

  const showList = !isMobile || mobileList || !tab;
  const showConversation = !isMobile || (!mobileList && !!tab);

  const body = (
    <Box component={embedded ? "div" : "main"} className={embedded ? undefined : "c4u-main"} sx={{ flexGrow: 1, display: "flex", flexDirection: "column", minHeight: 0, p: embedded ? 0 : { xs: 0, md: 2 }, gap: 1 }}>
      {data && !data.agentId && data.unavailable && <Alert severity="info">{data.unavailable}</Alert>}
      {!data && loadError && <Alert severity="error">{loadError}</Alert>}
      {/* No Super Assistente a área ocupa o espaço inteiro, como Agenda e Livro-caixa; o cartão é só da página antiga */}
      <Box sx={{ flexGrow: 1, minHeight: 0, display: "flex", bgcolor: colors.white, borderRadius: embedded ? 0 : { xs: 0, md: "8px" }, boxShadow: embedded ? "none" : { xs: "none", md: shadows.base }, overflow: "hidden" }}>
        {showList && explorer}
        {showConversation && conversation}
      </Box>
    </Box>
  );

  return (
    // dvh: no Safari do iPhone o 100vh conta a área sob a barra do navegador e escondia a caixa de texto
    <Box sx={embedded
      ? { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }
      : { display: "flex", flexDirection: "column", height: "100vh", "@supports (height: 100dvh)": { height: "100dvh" }, bgcolor: colors.background }}>
      {!embedded && <HeaderInternal showCTAButton={false} className="login-header" isLoggedIn={true} notificationCount={0} onRevalidateLogin={handleRevalidateLogin} onLogoClick={goToDashboard} />}
      {body}
      {historyDialog}
      <ConfirmModal
        isOpen={!!closingTab}
        type="warning"
        title={closingEnds ? `Encerrar a sessão de ${closingName}?` : `Fechar a aba de ${closingName}?`}
        message={(closingEnds
          ? "A conversa fica no Histórico, só para leitura. Para continuar depois, abra uma sessão nova."
          : "A aba sai da tela; nada é apagado.")
          + (closingTab?.draft.trim() ? " O texto ainda não enviado será descartado." : "")}
        confirmButtonText={closingEnds ? "Encerrar" : "Fechar"}
        cancelButtonText="Cancelar"
        isProcessing={ending}
        onCancel={() => setClosingId(null)}
        onConfirm={confirmClose}
      />
    </Box>
  );
};

export default PatientAssistant;
